import { Checkbox } from "antd";
import { ChevronDown } from "lucide-react";

import type { MenuTreeNodeVO } from "@/api/system/menus/type";
import { IconComponent } from "@/components/IconComponent";
import { cn } from "@/lib/cn";

import {
  getDescendantPermissionIds,
  getMenuCheckState,
  getMenuPermissionIds,
  getNodeCheckState,
  getPageCheckState,
  getPagePermissionIds,
  groupMenuChildrenForLayout,
} from "./utils";

/** 可编辑权限树 props */
export interface PermissionTreeProps {
  /** 菜单树节点列表 */
  nodes: MenuTreeNodeVO[];
  /** 已选权限 ID 列表 */
  selectedPermissions: string[];
  /** 切换权限勾选状态回调 */
  onToggle: (code: string, checked: boolean, childCodes: string[]) => void;
  /** 已展开的节点 ID 集合 */
  expandedIds: Set<string>;
  /** 切换节点展开状态回调 */
  onToggleExpand: (id: string) => void;
  /** 只读模式，不可勾选 */
  readOnly?: boolean;
}

/** 按钮级权限 chip */
const PermissionButtonChip = ({
  node,
  selectedPermissions,
  readOnly,
  onToggle,
}: {
  node: MenuTreeNodeVO;
  selectedPermissions: string[];
  readOnly: boolean;
  onToggle: PermissionTreeProps["onToggle"];
}) => {
  const state = getPageCheckState(node, selectedPermissions);
  const permissionIds = getPagePermissionIds(node);
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-sm transition-all border",
        readOnly ? "cursor-default" : "cursor-pointer",
        state === "checked"
          ? "bg-emerald-100 text-emerald-700 border-emerald-300"
          : "bg-white text-slate-500 border-slate-200",
        !readOnly && state !== "checked" && "hover:border-emerald-300 hover:text-emerald-600",
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (!readOnly) onToggle(node.id, state !== "checked", permissionIds);
      }}
    >
      <Checkbox checked={state === "checked"} disabled={readOnly} />
      <span>{node.menuName}</span>
    </button>
  );
};

/**
 * 树形权限复选框（菜单 / 页面 / 按钮三级）
 */
export const PermissionTree = ({
  nodes,
  selectedPermissions,
  onToggle,
  expandedIds,
  onToggleExpand,
  readOnly = false,
}: PermissionTreeProps) => (
  <div className="space-y-0">
    {nodes.map((menu) => {
      /** 菜单状态 */
      const menuState = getMenuCheckState(menu, selectedPermissions);
      /** 菜单是否展开 */
      const menuExpanded = expandedIds.has(menu.id);
      /** 菜单子级权限 ID 列表 */
      const childIds = getMenuPermissionIds(menu);
      /** 菜单直接子级 */
      const directChildren = menu.children ?? [];
      /** 直接子级中已选（含全选/半选）的数量，用于导航级角标 */
      const selectedDirectCount = directChildren.filter(
        (child) => getNodeCheckState(child, selectedPermissions) !== "unchecked",
      ).length;
      /** 菜单图标名 */
      const menuIconName = menu.icon ?? "";

      return (
        <div key={menu.id}>
          <div
            className={cn(
              "flex items-center gap-2 px-3 py-2.5 rounded-md transition-colors cursor-pointer",
              menuState !== "unchecked" ? "bg-orange-50/50" : "hover:bg-orange-100/50",
            )}
          >
            {menu.menuLevel === "NAVIGATION" ? (
              <>
                {menu.children ? (
                  <ChevronDown
                    onClick={() => onToggleExpand(menu.id)}
                    className={cn(
                      "w-4 h-4 text-slate-400 transition-transform shrink-0",
                      !menuExpanded && "-rotate-90",
                    )}
                  />
                ) : (
                  <span className="w-4 h-4 shrink-0" />
                )}
                <Checkbox
                  checked={menuState === "checked"}
                  indeterminate={menuState === "indeterminate"}
                  disabled={readOnly}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => {
                    if (readOnly) return;
                    onToggle(menu.id, menuState !== "checked", childIds);
                  }}
                />
                {menuIconName && (
                  <IconComponent
                    name={menuIconName}
                    className={cn(
                      "w-4 h-4 shrink-0",
                      menuState !== "unchecked" ? "text-emerald-600" : "text-slate-400",
                    )}
                  />
                )}
                <span
                  className={cn(
                    "flex-1",
                    menuState !== "unchecked" ? "text-emerald-700 font-medium" : "text-slate-700",
                  )}
                >
                  {menu.menuName}
                </span>
              </>
            ) : (
              <PermissionButtonChip
                node={menu}
                selectedPermissions={selectedPermissions}
                readOnly={readOnly}
                onToggle={onToggle}
              />
            )}
            {directChildren.length > 0 && (
              <span
                className={cn(
                  "text-xs font-medium",
                  selectedDirectCount > 0 ? "text-emerald-600" : "text-slate-400",
                )}
              >
                {selectedDirectCount}/{directChildren.length}
              </span>
            )}
          </div>
          {menu.children && menuExpanded && (
            <div className="bg-slate-50/30">
              {groupMenuChildrenForLayout(menu.children).map((group) => {
                if (group.type === "buttons") {
                  return (
                    <div
                      key={group.nodes.map((n) => n.id).join("-")}
                      className="flex flex-wrap gap-2 pl-8 pr-3 py-2"
                    >
                      {group.nodes.map((btn) => (
                        <PermissionButtonChip
                          key={btn.id}
                          node={btn}
                          selectedPermissions={selectedPermissions}
                          readOnly={readOnly}
                          onToggle={onToggle}
                        />
                      ))}
                    </div>
                  );
                }
                /** 页面节点 */
                const page = group.node;
                /** 页面状态 */
                const pageState = getPageCheckState(page, selectedPermissions);
                /** 页面是否展开 */
                const pageExpanded = expandedIds.has(page.id);
                /** 页面权限 ID 列表 */
                const pagePermissionIds = getPagePermissionIds(page);
                /** 页面子级权限 ID 列表 */
                const pageDescendantIds = getDescendantPermissionIds(page);
                /** 页面子级中已选（含全选/半选）的数量，用于页面级角标 */
                const pageSelectedCount = pageDescendantIds.filter((id) =>
                  selectedPermissions.includes(id),
                ).length;
                /** 页面图标名 */
                const pageIconName = page.icon ?? "";

                return (
                  <div key={page.id}>
                    <div
                      className={cn(
                        "flex items-center gap-2 pl-8 pr-3 py-2 transition-colors cursor-pointer",
                        pageState !== "unchecked" ? "bg-slate-50/30" : "hover:bg-slate-100/50",
                      )}
                      onClick={() => onToggleExpand(page.id)}
                    >
                      {page.children ? (
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 text-slate-400 transition-transform shrink-0",
                            !pageExpanded && "-rotate-90",
                          )}
                        />
                      ) : (
                        <span className="w-4 h-4 shrink-0" />
                      )}
                      <Checkbox
                        checked={pageState === "checked"}
                        indeterminate={pageState === "indeterminate"}
                        disabled={readOnly}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (readOnly) return;
                          onToggle(page.id, pageState !== "checked", pagePermissionIds);
                        }}
                      />
                      {pageIconName && (
                        <IconComponent
                          name={pageIconName}
                          className={cn(
                            "w-4 h-4 shrink-0",
                            pageState !== "unchecked" ? "text-emerald-600" : "text-slate-400",
                          )}
                        />
                      )}
                      <span
                        className={cn(
                          "flex-1",
                          pageState !== "unchecked"
                            ? "text-emerald-700 font-medium"
                            : "text-slate-600",
                        )}
                      >
                        {page.menuName}
                      </span>
                      {pageDescendantIds.length > 0 && (
                        <span
                          className={cn(
                            "text-xs font-medium",
                            pageSelectedCount > 0 ? "text-emerald-600" : "text-slate-400",
                          )}
                        >
                          {pageSelectedCount}/{pageDescendantIds.length}
                        </span>
                      )}
                    </div>
                    {page.children && pageExpanded && (
                      <div className="flex flex-wrap gap-2 pl-14 pr-3 py-2">
                        {page.children.map((btn) => (
                          <PermissionButtonChip
                            key={btn.id}
                            node={btn}
                            selectedPermissions={selectedPermissions}
                            readOnly={readOnly}
                            onToggle={onToggle}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    })}
  </div>
);
